import { useEffect, useState } from 'react';

import { type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

import { PageTitle } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import {
  DialogAllUnCancelable,
  DialogCanceledStatusIncludedSpecifications,
  DialogCanceledStatusSpecifications,
  DialogPartialCancelable,
  ReceiveLabelPrintModal,
  ReceiveRequestCancelModal,
  ReceiveRequestDetailModal,
  ReceiveRequestRegisterModal,
  ReceiveRequestSearchFilter,
  ReceiveRequestTable,
} from '~/components/receive';

import { useDownloadReady, useModal, useStableCallback, useToggle } from '~/hooks/common';
import {
  receiveKeys,
  useGetFilterAllQuery,
  useGetReceivingLabelItemsMutation,
  useGetReceivingRequestCopyMutation,
  useGetRequestsRequestedDownloadMutation,
  useGetSpecificationsMutation,
  usePostRequestsRequestedQuery,
} from '~/hooks/receive';
import { useReceiveStore, useUserInfoStore } from '~/store';
import {
  addIf,
  formatDate,
  getDateByOffset,
  getMatchingXDockCenter,
  isValidDateRange,
  renderFilterErrorMessage,
} from '~/utils';

import {
  BREAD_CRUMB,
  INITIAL_RECEIVE_PAGE_CURSOR,
  INITIAL_RECEIVE_PAGE_SIZE,
  MAX_RECEIVE_DATE_RANGE,
  OPTION_ALL,
  PATH,
  RECEIVE,
  RECEIVE_X_DOCK_CLUSTER_CODE,
  TOAST_MESSAGE,
} from '~/constants';

import type {
  TypePostRequestsRequestedParams,
  TypeReceiveRequestsFilter,
  TypeReceiveRequestsFilterStatus,
  TypeReceiveRequestsInfo,
  TypeReceivingLabelItem,
} from '~/types/receive.types';

const DEFAULT_RECEIVE_REQUESTS_FILTER: TypeReceiveRequestsFilter = {
  keywordType: null,
  keyword: '',
  center: OPTION_ALL,
  temperature: OPTION_ALL,
  dateType: null,
  dateRange: { start: getDateByOffset(-1, 'day'), end: getDateByOffset(0, 'day') },
  status: OPTION_ALL,
  releaseCode: OPTION_ALL,
  xDock: OPTION_ALL,
  pagination: {
    currentPage: INITIAL_RECEIVE_PAGE_CURSOR,
    totalCount: 0,
    pageSize: INITIAL_RECEIVE_PAGE_SIZE,
  },
};

const DEFAULT_RECEIVE_REQUESTS_FILTER_STATUS: TypeReceiveRequestsFilterStatus = {
  dateRange: 'default',
};

/**
 * [입고관리 > 입고요청관리] 페이지 컴포넌트
 */
const ReceiveRequestsPage = () => {
  const queryClient = useQueryClient();

  const specificationsMutation = useGetSpecificationsMutation();
  const downloadRequestsExcelMutation = useGetRequestsRequestedDownloadMutation();
  const copyRequestMutation = useGetReceivingRequestCopyMutation();
  const labelItemsMutation = useGetReceivingLabelItemsMutation();

  const ownerCode = useUserInfoStore((state) => state.userInfo.ownerCode);

  const { data: filterOptions, isError: isFilterOptionsError } = useGetFilterAllQuery('RECEIVING_REQUEST', {
    showGlobalErrorToast: false,
  });
  const { data: addReceivingGoodsOptions, isError: isAddReceivingGoodsOptionsError } = useGetFilterAllQuery(
    'ADD_RECEIVING_GOODS',
    { showGlobalErrorToast: false }
  );

  const [listQueryParams, setListQueryParams] = useState<TypePostRequestsRequestedParams | null>(null);

  const listQuery = usePostRequestsRequestedQuery({
    ownerCode,
    params: listQueryParams ?? ({} as TypePostRequestsRequestedParams),
    enabled: !!listQueryParams && !!ownerCode,
  });

  const [setRequestModalDefaultInfoValue, setRequestModalGoodsList, setDetailModalOrderGoodsList] = useReceiveStore(
    useShallow((state) => [
      state.setRequestModalDefaultInfoValue,
      state.setRequestModalGoodsList,
      state.setDetailModalOrderGoodsList,
    ])
  );

  const [selectedDetailRequestId, setSelectedDetailRequestId] = useState<string | null>(null);

  const [registerModalTemperatureOptions, setRegisterModalTemperatureOptions] = useState<
    TypeSelectOption<string>[] | null
  >(null);

  const [receiveRequestsFilter, setReceiveRequestsFilter] = useState<TypeReceiveRequestsFilter>(
    DEFAULT_RECEIVE_REQUESTS_FILTER
  );
  const [receiveRequestsFilterStatus, setReceiveRequestsFilterStatus] = useState<TypeReceiveRequestsFilterStatus>(
    DEFAULT_RECEIVE_REQUESTS_FILTER_STATUS
  );

  const [selectedItems, setSelectedItems] = useState<TypeReceiveRequestsInfo[]>([]);
  const [cancelItems, setCancelItems] = useState<TypeReceiveRequestsInfo[]>([]);
  const [receivingLabelItems, setReceivingLabelItems] = useState<TypeReceivingLabelItem[] | null>(null);

  const { isDownloadReady, searchedParam, markDownloadReady, checkFilterChange } = useDownloadReady<
    TypeReceiveRequestsFilter,
    TypePostRequestsRequestedParams
  >({
    initialFilter: DEFAULT_RECEIVE_REQUESTS_FILTER,
  });

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const requestCancelModal = useModal({
    onConfirm: () => handleSearch(),
  });

  const requestRegisterModal = useModal({
    onConfirm: () => {
      const initializedFilter = {
        ...DEFAULT_RECEIVE_REQUESTS_FILTER,
        keywordType: filterOptions?.keywordType[0] ?? null,
        dateType: filterOptions?.dateType[0] ?? null,
      };

      handleSearch(initializedFilter);
    },
  });

  const requestDetailModal = useModal({
    onCloseCompleted: () => {
      setDetailModalOrderGoodsList([]);
      setSelectedDetailRequestId(null);
    },
    onConfirm: () => {
      const initializedFilter = {
        ...DEFAULT_RECEIVE_REQUESTS_FILTER,
        keywordType: filterOptions?.keywordType[0] ?? null,
        dateType: filterOptions?.dateType[0] ?? null,
      };

      handleSearch(initializedFilter);
    },
  });

  const receivingLabelPrintModal = useModal();

  const handleSearch = (filterInfo: TypeReceiveRequestsFilter = receiveRequestsFilter) => {
    setReceiveRequestsFilter(filterInfo);
    setSelectedItems([]);

    const params: TypePostRequestsRequestedParams = {
      ...addIf(filterInfo.center.value, 'cluster'),
      ...addIf(filterInfo.temperature.value, 'warehouse'),
      ...addIf(filterInfo.keywordType?.value, 'codeType'),
      ...addIf(filterInfo.keyword, 'code'),
      dateType: filterInfo.dateType?.value ?? '',
      dateFrom: formatDate(filterInfo.dateRange?.start, 'YYYY-MM-DD') ?? '',
      dateTo: formatDate(filterInfo.dateRange?.end, 'YYYY-MM-DD') ?? '',
      ...addIf(filterInfo.status.value, 'status'),
      ...addIf(filterInfo.releaseCode.value, 'releaseCode'),
      ...addIf(filterInfo.xDock.value, 'xdock', filterInfo.xDock.value === 'YES'),
      limit: filterInfo.pagination.pageSize,
      offset: filterInfo.pagination.currentPage * filterInfo.pagination.pageSize,
    };

    queryClient.invalidateQueries({
      queryKey: receiveKeys.requestsRequested(ownerCode, params),
    });

    setListQueryParams(params);
  };

  const [prevFilterOptions, setPrevFilterOptions] = useState<typeof filterOptions | null>(null);
  if (filterOptions !== prevFilterOptions) {
    setPrevFilterOptions(filterOptions);
    if (filterOptions) {
      setReceiveRequestsFilter((prev) => ({
        ...prev,
        keywordType: prev.keywordType ?? filterOptions.keywordType[0],
        dateType: prev.dateType ?? filterOptions.dateType[0],
      }));
    }
  }

  const hasFilterError = isFilterOptionsError || isAddReceivingGoodsOptionsError;

  useEffect(() => {
    if (hasFilterError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [hasFilterError]);

  const handleFilterChange = useStableCallback(() => checkFilterChange(receiveRequestsFilter));

  useEffect(() => {
    handleFilterChange();
  }, [receiveRequestsFilter, handleFilterChange]);

  const syncDownloadReady = useStableCallback(() => {
    if (listQuery.data && listQueryParams && !listQuery.isPlaceholderData) {
      markDownloadReady({
        filterValue: receiveRequestsFilter,
        hasData: listQuery.data.data.receivingRequests.length > 0,
        searchedParam: listQueryParams,
      });
    }
  });

  useEffect(() => {
    syncDownloadReady();
  }, [listQuery.data, listQueryParams, listQuery.isPlaceholderData, syncDownloadReady]);

  const downloadSpecifications = (items: TypeReceiveRequestsInfo[]) => {
    const ids = items.map((item) => item.requestId).join(',');

    specificationsMutation.mutate(
      { ownerCode, ids },
      {
        onSuccess: () => toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS }),
      }
    );
  };

  const handleOpenRequestDetail = (data?: TypeReceiveRequestsInfo) => {
    if (!data) return;

    setSelectedDetailRequestId(String(data.requestId));
    requestDetailModal.handleOpenModal();
  };

  const handleFilterReset = () => {
    setReceiveRequestsFilter({
      ...DEFAULT_RECEIVE_REQUESTS_FILTER,
      keywordType: filterOptions?.keywordType[0] ?? null,
      dateType: filterOptions?.dateType[0] ?? null,
      pagination: receiveRequestsFilter.pagination,
    });
    setReceiveRequestsFilterStatus({
      ...DEFAULT_RECEIVE_REQUESTS_FILTER_STATUS,
    });
  };

  const handleClickSpecificationDownload = () => {
    const isDownloadableStatus = (item: TypeReceiveRequestsInfo) => {
      return item.status === 'REQUESTED' || item.status === 'CONFIRMED';
    };
    const isUnDownloadableStatus = (item: TypeReceiveRequestsInfo) => item.status === 'CANCELED';

    // 요청상태가 [요청완료, 확인완료]인 항목만 선택된 경우
    if (selectedItems.every(isDownloadableStatus)) {
      downloadSpecifications(selectedItems);
    }
    // 요청상태가 [요청취소]인 항목만 선택된 경우
    else if (selectedItems.every(isUnDownloadableStatus)) {
      dialogService.addDialog({
        children: <DialogCanceledStatusSpecifications />,
      });
    } else {
      dialogService.addDialog({
        type: 'confirm',
        children: <DialogCanceledStatusIncludedSpecifications />,
        onConfirm: () => {
          const downloadableItems = selectedItems.filter(isDownloadableStatus);

          downloadSpecifications(downloadableItems);
        },
      });
    }
  };

  /**
   * 입고요청 취소 가능 조건
   * - [요청완료]
   * - [확인완료] + 입고예정일 하루 전(D-1) 17시 이전
   *
   * 입고요청 취소 불가능 조건
   * - [요청취소]
   * - [확인완료] + 입고예정일 하루 전(D-1) 17시 이후
   */
  const handleClickRequestCancel = () => {
    const isBeforeCutoffTime = ({ receivingExpectedDate }: TypeReceiveRequestsInfo) => {
      // 입고예정일 D-1 17시 이전인지 판별
      if (!receivingExpectedDate) return false;
      const cutoffTime = dayjs(receivingExpectedDate).subtract(1, 'day').hour(17).startOf('hour');
      return dayjs().isBefore(cutoffTime);
    };

    const isCancelableStatus = (item: TypeReceiveRequestsInfo) => {
      return item.status === 'REQUESTED' || (item.status === 'CONFIRMED' && isBeforeCutoffTime(item));
    };
    const isUnCancelableStatus = (item: TypeReceiveRequestsInfo) => {
      return item.status === 'CANCELED' || (item.status === 'CONFIRMED' && !isBeforeCutoffTime(item));
    };

    const handleRequestCancel = (items: TypeReceiveRequestsInfo[]) => {
      setCancelItems(items);
      requestCancelModal.handleOpenModal();
    };

    // 모든 건이 입고요청 취소 가능
    if (selectedItems.every(isCancelableStatus)) {
      handleRequestCancel(selectedItems);
      return;
    }

    // 모든 건이 입고요청 취소 불가능
    if (selectedItems.every(isUnCancelableStatus)) {
      dialogService.addDialog({
        children: <DialogAllUnCancelable />,
      });
      return;
    }

    // 일부 건이 입고요청 취소 가능
    dialogService.addDialog({
      type: 'confirm',
      children: <DialogPartialCancelable />,
      onConfirm: () => {
        const cancelableItems = selectedItems.filter(isCancelableStatus);
        handleRequestCancel(cancelableItems);
      },
    });
  };

  const handleClickRequestCopy = () => {
    const requestIds = selectedItems.map((item) => item.requestId);

    copyRequestMutation.mutate(
      { ownerCode, requestIds: requestIds.join(',') },
      {
        onSuccess: (data) => {
          const isUsingXDock = data.xdock;

          setRequestModalDefaultInfoValue({
            center: addReceivingGoodsOptions?.center.find(({ value }) => value === data.cluster) ?? OPTION_ALL,
            temperature:
              addReceivingGoodsOptions?.temperature.find(({ value }) => value === data.warehouse) ?? OPTION_ALL,
            xDock: isUsingXDock ? 'true' : 'false',
            ...(isUsingXDock && {
              xDockCenter: getMatchingXDockCenter(
                addReceivingGoodsOptions?.groupedTemperatureByCenter[RECEIVE_X_DOCK_CLUSTER_CODE] ?? [],
                data.xdockCenter,
                false
              ),
            }),
            supplierCode: addReceivingGoodsOptions?.supplier.find(({ value }) => value === data.supplierCode) ?? null,
            note: '',
          });
          setRequestModalGoodsList(
            data.orderItems.map((item, index) => {
              const dateTypeMap = { EXPIRATION_DATE: 'expirationDate', MANUFACTURE_DATE: 'manufactureDate' } as const;
              const dateType = item.dateType ? dateTypeMap[item.dateType] : null;

              return {
                ...item,
                index: index + 1,
                dateType: item.dateUseYn ? dateType : null,
                date: item.dateUseYn && dateType ? item[dateType] : null,
                palletYn: item.usePallet,
                lotNo: item.lotNo ?? '',
              };
            })
          );

          setRegisterModalTemperatureOptions(addReceivingGoodsOptions?.groupedTemperatureByCenter[data.cluster] ?? []);

          requestRegisterModal.handleOpenModal();

          // 복사 시 성공 알림 메시지가 있으면 다이얼로그 표시 (사용중단 → 중복 SKU 순서)
          const { deactivatedSku, duplicateSku } = data.notice ?? {};
          if (deactivatedSku && duplicateSku) {
            dialogService.addDialog({
              children: deactivatedSku,
              onConfirmCompleted: () => {
                dialogService.addDialog({ children: duplicateSku });
              },
            });
          } else if (deactivatedSku || duplicateSku) {
            dialogService.addDialog({
              children: deactivatedSku || duplicateSku,
            });
          }
        },
        onError: (error) => {
          const errorCode = error?.code;

          if (errorCode === RECEIVE.SKU_AND_QUANTITY_COUNT_EXCEEDED) {
            dialogService.addDialog({
              children:
                '선택하신 항목의 총합이 입고요청 품목 200개,\n입고 가능 수량 20,000개를 초과할 수 없습니다.\n수량을 나누어 입고 요청 복사 작업을\n진행해 주세요.',
            });
            return;
          }
          if (errorCode === RECEIVE.DIFFERENT_WAREHOUSE_INCLUDED) {
            dialogService.addDialog({
              children:
                '선택하신 항목 중 입고센터가 서로 다른 건이 포함되어 있습니다.\n입고센터별로 나누어 입고 요청 복사 작업을\n진행해 주세요.',
            });
            return;
          }
          if (errorCode === RECEIVE.DIFFERENT_SUPPLIER_INCLUDED) {
            dialogService.addDialog({
              children:
                '선택하신 항목 중 공급사가 서로 다른 건이 포함되어 있습니다.\n공급사별로 나누어 입고 요청 복사 작업을\n진행해 주세요.',
            });
            return;
          }
          if (errorCode === RECEIVE.DIFFERENT_CENTER_AND_SUPPLIER_INCLUDED) {
            dialogService.addDialog({
              children:
                '선택하신 항목 중 입고센터와 공급사 모두 서로 다른 건이 포함되어 있습니다.\n입고센터별, 공급사별로 나누어 입고 요청 복사 작업을 진행해 주세요.',
            });
            return;
          }

          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          });
        },
      }
    );
  };

  const handleClickPrintReceivingLabel = () => {
    const confirmedItems = selectedItems.filter((item) => item.status === 'CONFIRMED');

    const fetchReceivingLabelItems = () => {
      labelItemsMutation.mutate(
        { ownerCode, receivingRequestCodes: confirmedItems.map((item) => item.requestCode).join(',') },
        {
          onSuccess: (data) => {
            setReceivingLabelItems(data.receivingRequestItems);
            receivingLabelPrintModal.handleOpenModal();
          },
        }
      );
    };

    if (confirmedItems.length === 0) {
      // 확인완료 항목이 하나도 없는 경우
      dialogService.addDialog({
        children: '확인완료 상태인 요청 건만 라벨 출력이 가능합니다.',
      });
      return;
    }
    if (confirmedItems.length === selectedItems.length) {
      // 확인완료 항목만 선택된 경우: 바로 출력
      fetchReceivingLabelItems();
      return;
    }

    // 일부만 확인완료인 경우: 확인 다이얼로그 표시
    dialogService.addDialog({
      type: 'confirm',
      children: '확인완료 상태가 아닌 요청 건이 선택되어 있습니다. 확인완료 상태인 항목만 라벨을 출력하시겠습니까?',
      onConfirm: fetchReceivingLabelItems,
    });
  };

  const handleChangeTableRowSelection = (selectedRows: TypeReceiveRequestsInfo[]) => {
    setSelectedItems(selectedRows);
  };

  const handleChangePageLimit = (option: TypeSelectOption<number>) => {
    const updatedFilter: TypeReceiveRequestsFilter = {
      ...receiveRequestsFilter,
      pagination: {
        ...receiveRequestsFilter.pagination,
        pageSize: option.value,
        currentPage: INITIAL_RECEIVE_PAGE_CURSOR,
      },
    };

    handleSearch(updatedFilter);
  };

  const handleClickPage = (value: number) => {
    const updatedFilter: TypeReceiveRequestsFilter = {
      ...receiveRequestsFilter,
      pagination: {
        ...receiveRequestsFilter.pagination,
        currentPage: value,
      },
    };

    handleSearch(updatedFilter);
  };

  const validateDateRange = (): boolean => {
    if (!isValidDateRange(receiveRequestsFilter.dateRange, MAX_RECEIVE_DATE_RANGE)) {
      setReceiveRequestsFilterStatus((prev) => ({ ...prev, dateRange: 'error' }));

      dialogService.addDialog({ children: `최대 ${MAX_RECEIVE_DATE_RANGE}일 기간으로 입력 후 검색해주세요.` });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    const isValidForm = validateDateRange();

    if (isValidForm) {
      const updatedFilter = {
        ...receiveRequestsFilter,
        pagination: {
          ...receiveRequestsFilter.pagination,
          currentPage: INITIAL_RECEIVE_PAGE_CURSOR,
        },
      };
      handleSearch(updatedFilter);
    }
  };

  const handleClickExcelDownload = () => {
    if (!searchedParam) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { limit, offset, ...restParams } = searchedParam;

    downloadRequestsExcelMutation.mutate(
      { ownerCode, ...restParams },
      {
        onSuccess: () =>
          toastService.addToast({
            message: TOAST_MESSAGE.DOWNLOAD_SUCCESS,
          }),
      }
    );
  };

  return (
    <PageWrapper>
      <PageTitle
        title="입고 요청 관리"
        breadcrumbs={BREAD_CRUMB[PATH.RECEIVE_REQUESTS]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <ReceiveRequestSearchFilter
          options={filterOptions}
          values={receiveRequestsFilter}
          valuesState={receiveRequestsFilterStatus}
          onChange={(values) => setReceiveRequestsFilter((prev) => ({ ...prev, ...values }))}
          onChangeStatus={(values) => setReceiveRequestsFilterStatus((prev) => ({ ...prev, ...values }))}
          onSearch={handleSubmit}
          onReset={handleFilterReset}
        />
      )}
      <ReceiveRequestTable
        data={listQuery.data?.data.receivingRequests ?? null}
        pagination={{
          ...receiveRequestsFilter.pagination,
          totalCount: listQuery.data?.pagination.listTotal ?? 0,
        }}
        isDownloadReady={isDownloadReady}
        selectedItems={selectedItems}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleClickPage}
        onChangeRowSelection={handleChangeTableRowSelection}
        onClickViewDetail={handleOpenRequestDetail}
        onClickSpecificationDownload={handleClickSpecificationDownload}
        onClickExcelDownload={handleClickExcelDownload}
        onClickRequestCancel={handleClickRequestCancel}
        onClickRequestCopy={handleClickRequestCopy}
        onClickRequestRegister={requestRegisterModal.handleOpenModal}
        onClickPrintReceivingLabel={handleClickPrintReceivingLabel}
      />
      {requestCancelModal.isOpen && (
        <ReceiveRequestCancelModal
          ownerCode={ownerCode}
          requestCodes={cancelItems.map((item) => item.requestCode)}
          open={requestCancelModal.isOpen}
          onCancel={requestCancelModal.handleCloseModal}
          onConfirm={requestCancelModal.handleConfirmModal}
        />
      )}
      {requestRegisterModal.isOpen && (
        <ReceiveRequestRegisterModal
          open={requestRegisterModal.isOpen}
          ownerCode={ownerCode}
          temperatureOptionsByCurrentCenter={registerModalTemperatureOptions}
          onCancel={requestRegisterModal.handleCloseModal}
          onConfirm={requestRegisterModal.handleConfirmModal}
        />
      )}
      {requestDetailModal.isOpen && selectedDetailRequestId && (
        <ReceiveRequestDetailModal
          requestId={selectedDetailRequestId}
          open={requestDetailModal.isOpen}
          onCancel={requestDetailModal.handleCloseModal}
          onConfirm={requestDetailModal.handleConfirmModal}
        />
      )}
      {receivingLabelPrintModal.isOpen && receivingLabelItems && (
        <ReceiveLabelPrintModal
          open={receivingLabelPrintModal.isOpen}
          data={receivingLabelItems}
          onCancel={receivingLabelPrintModal.handleCloseModal}
          onConfirm={receivingLabelPrintModal.handleConfirmModal}
        />
      )}
    </PageWrapper>
  );
};

export default ReceiveRequestsPage;
